import React, { useState } from "react";
import { Tabs, Space, Button, Row, Col, TabsProps, Typography } from "antd";

import type { ColumnsType } from "antd/es/table";
import { ProfileOutlined, EyeOutlined, EditOutlined } from "@ant-design/icons";
import { useGetComplaintsQuery } from "../../../redux/services/complaintApi";
import TopScrollTable from "../../../components/TopScrollTable";
import Filters from "../Filters";
import { Link, Outlet, useNavigate } from "react-router-dom";
import {
  getStatusText,
  localizeDate,
  getQuerySearchParams,
  applyPaginationToQueryParams
} from "../../../utils";
import { useAppSelector } from "../../../redux/hooks";

const { Text } = Typography;

const items: TabsProps["items"] = [
  {
    key: "1",
    label: "All Complaints"
  },
  {
    key: "2",
    label: "Un-Assigned Complaints"
  }
];

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

const Complaint = () => {
  const querySearchParams = getQuerySearchParams();
  const [queryParams, setQueryParams] = useState<any>({
    include_all: true,
    page: parseInt(querySearchParams.page) || 1,
    page_size: parseInt(querySearchParams.page_size) || 20
  });
  const navigate = useNavigate();

  const { data, isLoading, isFetching } = useGetComplaintsQuery({
    ...queryParams
  });
  const locations = data?.complaints || [];
  const user = useAppSelector<any>((store) => store.auth.user);
  const role_types = user.roles.map((role: any) => role.role_type);
  const isManager = !role_types
    .map((role_type: any) => ["city_manager", "zonal_manager"].includes(role_type))
    .includes(false);

  const isClient = user?.user_type === "client";

  const columns: ColumnsType<DataType> = [
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      render: (brand) => <Text>{brand ? brand?.name : "-"}</Text>
    },
    {
      title: "Complaint Code",
      dataIndex: "ecb_complaint_code",
      key: "ecb_complaint_code"
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "EV City",
      dataIndex: "city",
      key: "city",
      render: (city) => <>{city?.name}</>
    },
    {
      title: "City Manager",
      dataIndex: "assigned_city_manager",
      key: "assigned_city_manager",
      render: (assigned_city_manager) => (
        <Text>{assigned_city_manager ? assigned_city_manager?.name : "-"}</Text>
      )
    },
    {
      title: "Installation Date",
      dataIndex: "actual_install_date_time",
      key: "actual_install_date_time",
      render: (actual_install_date_time) => (
        <Text>{actual_install_date_time ? localizeDate(actual_install_date_time) : "-"}</Text>
      )
    },
    {
      title: "Complaint Rcvd Date",
      dataIndex: "complaint_receive_date",
      key: "complaint_receive_date",
      render: (complaint_receive_date) => (
        <Text>{complaint_receive_date ? localizeDate(complaint_receive_date) : "-"}</Text>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, complaint: any) => <>{getStatusText(complaint)}</>
    },
    {
      title: "Action",
      key: "action",
      render: (_, complaint: any) => (
        <Space size="middle" direction="horizontal">
          {isClient ? (
            <>
              <Link to={`${complaint?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
            </>
          ) : (
            <>
              <Link to={`${complaint?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              {!isManager && (
                <Link to={`${complaint?.id}/update`}>
                  <Button icon={<EditOutlined />} title="Update"></Button>
                </Link>
              )}
              {complaint.status !== "closed" && (
                <Link to={`${complaint?.id}/create-visit`}>
                  <Button icon={<ProfileOutlined />} title="Create Visit"></Button>
                </Link>
              )}
            </>
          )}
        </Space>
      )
    }
  ];

  const clientColumns: ColumnsType<DataType> = [
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      render: (brand) => <Text>{brand ? brand?.name : "-"}</Text>
    },
    {
      title: "Complaint Code",
      dataIndex: "ecb_complaint_code",
      key: "ecb_complaint_code"
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "EV City",
      dataIndex: "city",
      key: "city",
      render: (city) => <>{city?.name}</>
    },
    {
      title: "Complaint Rcvd Date",
      dataIndex: "complaint_receive_date",
      key: "complaint_receive_date",
      render: (complaint_receive_date) => (
        <Text>{complaint_receive_date ? localizeDate(complaint_receive_date) : "-"}</Text>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, complaint: any) => <>{getStatusText(complaint)}</>
    },
    {
      title: "Action",
      key: "action",
      render: (_, complaint: any) => (
        <Space size="middle" direction="horizontal">
          {isClient ? (
            <>
              <Link to={`${complaint?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
            </>
          ) : (
            <>
              <Link to={`${complaint?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              {!isManager && (
                <Link to={`${complaint?.id}/update`}>
                  <Button icon={<EditOutlined />} title="Update"></Button>
                </Link>
              )}
              {complaint.status !== "closed" && (
                <Link to={`${complaint?.id}/create-visit`}>
                  <Button icon={<ProfileOutlined />} title="Create Visit"></Button>
                </Link>
              )}
            </>
          )}
        </Space>
      )
    }
  ];

  const onChange = (key: string) => {
    if (key === "1") {
      const values: any = { unassigned: null };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
    if (key === "2") {
      const values: any = { unassigned: true };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
  };

  const pagination = {
    pageSize: parseInt(querySearchParams.page_size) || 20,
    current: parseInt(querySearchParams.page) || 1,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onChange: (page: number, pageSize: number) => {
      const values = {
        page,
        page_size: pageSize
      };

      setQueryParams((old: any) => ({ ...old, ...values }));

      applyPaginationToQueryParams({
        ...queryParams,
        ...values,
        navigate
      });
    }
  };

  return (
    <>
      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <div>
            <h2>Complaints</h2>
            <Text>Total complaints: {data?.meta?.total_count || 0}</Text>
          </div>
        </Col>
      </Row>

      <Row>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <Tabs defaultActiveKey="1" items={items} onChange={onChange} />
          {(!isManager || !isClient) && (
            <Link to="upload-complaints">
              {!isClient && (
                <Button size="large" type="primary">
                  Upload Complaint
                </Button>
              )}
            </Link>
          )}
        </Col>
      </Row>

      <Filters
        applyFilters={(values: any) =>
          setQueryParams((oldValues: any) => ({ ...oldValues, ...values}))
        }
        complaints
      />

      <TopScrollTable
        rowKey="id"
        columns={isClient ? clientColumns : columns}
        dataSource={locations}
        bordered
        loading={isLoading || isFetching}
        scroll={{ x: 400 }}
        pagination={pagination}
      />
      <Outlet />
    </>
  );
};

export default Complaint;
